import { useState } from "react";

interface ServiceCardProps {
  icon: string;
  title: string;
  description?: string;
  desc?: string;
  variant: "light" | "dark" | "blue";
}

export default function ServiceCard({ icon, title, description, desc, variant }: ServiceCardProps) {
  const [hovered, setHovered] = useState(false);
  const body = description || desc || "";

  const handleClick = () => {
    document.querySelector("#easy")?.scrollIntoView({ behavior: "smooth" });
  };

  const isLight = variant === "light";
  const isBlue = variant === "blue";

  const cardBg = isLight
    ? hovered ? "#4a9fd4" : "#fff"
    : isBlue
    ? hovered ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.12)"
    : hovered ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)";

  const titleColor = isLight
    ? hovered ? "#fff" : "#1a3a52"
    : "#fff";

  const descColor = isLight
    ? hovered ? "rgba(255,255,255,0.9)" : "#6b7280"
    : "rgba(255,255,255,0.75)";

  const borderColor = isLight
    ? hovered ? "#4a9fd4" : "rgba(74,159,212,0.15)"
    : "rgba(255,255,255,0.15)";

  return (
    <div
      className="relative flex flex-col gap-4 p-6 rounded-2xl cursor-pointer transition-all duration-300"
      onClick={handleClick}
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered
          ? isLight
            ? "0 16px 40px rgba(74,159,212,0.3)"
            : "0 16px 40px rgba(0,0,0,0.2)"
          : "none",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon */}
      <div
        className="w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 flex-shrink-0"
        style={{
          background: hovered
            ? isLight ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.15)"
            : isLight ? "rgba(74,159,212,0.08)" : "rgba(255,255,255,0.1)",
        }}
      >
        {icon && icon.startsWith("ri-") ? (
          <i
            className={`${icon} text-xl`}
            style={{ color: hovered ? "#fff" : isLight ? "#4a9fd4" : "#fff" }}
          />
        ) : (
          <img
            src={icon}
            alt={title}
            className="w-7 h-7 object-contain"
            style={{ filter: hovered ? "brightness(0) invert(1)" : isLight ? "none" : "brightness(0) invert(1)" }}
            onError={(e) => {
              const el = e.target as HTMLImageElement;
              el.style.display = "none";
              const parent = el.parentElement;
              if (parent) {
                const ic = document.createElement("i");
                ic.className = "ri-settings-3-line text-xl";
                ic.style.color = hovered ? "#fff" : "#4a9fd4";
                parent.appendChild(ic);
              }
            }}
          />
        )}
      </div>

      {/* Title */}
      <h3
        className="font-bold text-sm leading-snug transition-colors duration-300"
        style={{ color: titleColor }}
      >
        {title}
      </h3>

      {/* Description */}
      <p
        className="text-xs leading-relaxed transition-colors duration-300"
        style={{ color: descColor }}
      >
        {body}
      </p>

      {/* Arrow indicator */}
      <div
        className="flex items-center gap-1 text-xs font-semibold transition-all duration-300 mt-auto"
        style={{ color: hovered ? (isLight ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.7)") : "transparent" }}
      >
        Learn more <i className="ri-arrow-right-line"></i>
      </div>
    </div>
  );
}
